/**
 * The navigation model for the signed-in viewer (SA-960, spec §1.2).
 *
 * The first four selectors ARE the subscription. `useFeature` and
 * `useCanAccessFeature` re-render because they select a boolean, but the
 * helpers handed to `buildNav` here read their stores through get(). A hook
 * that called only the helpers would never re-render when the manifest or the
 * permission set loads. The helpers keep their "true before the store loads"
 * rule, so a surface's row still assumes yes while loading.
 */
import { useFeatureStore } from '@/stores/featureStore'
import { usePermissionStore } from '@/stores/permissionStore'
import { useNavBadgeStore } from '@/stores/navBadgeStore'
import { useRecordNoun } from '@/hooks/useRecordNoun'
import { buildNav, type NavModel, type NavSurface } from './navModel'

export function useNavModel(surfaces: readonly NavSurface[]): NavModel {
  useFeatureStore((s) => s.isLoaded)
  useFeatureStore((s) => s.features)
  usePermissionStore((s) => s.isLoaded)
  usePermissionStore((s) => s.permissions)
  const companiesPage = useFeatureStore((s) => s.companiesPage)
  const permissionsLoaded = usePermissionStore((s) => s.isLoaded)
  const inboxUnread = useNavBadgeStore((s) => s.inboxUnread)
  const inboxEscalated = useNavBadgeStore((s) => s.inboxEscalated)
  const ordersNew = useNavBadgeStore((s) => s.ordersNew)
  const recordNoun = useRecordNoun()

  const { isEnabled } = useFeatureStore.getState()
  const { hasPermission } = usePermissionStore.getState()
  return buildNav({
    has: isEnabled,
    can: (feature, permission) => isEnabled(feature) && hasPermission(permission),
    isAdmin: hasPermission('admin:access'),
    hasPermission,
    // Strict (SA-1456 spec §7.2): no Companies row until the manifest and the permissions have loaded.
    companiesPage: companiesPage && permissionsLoaded,
    recordNoun,
    surfaces,
    badges: { inboxUnread, inboxEscalated, ordersNew },
  })
}
